import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import Navbar from "../components/Navbar";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  ArrowLeft, Clock, FileText, BookOpen, Play, ClipboardList,
  CalendarDays, MessageSquareQuote, RefreshCw, ChevronRight, Sparkles, CheckCircle2
} from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ExamDetail() {
  const navigate = useNavigate();
  const { examId } = useParams();
  const token = localStorage.getItem("token");

  const [exam, setExam] = useState(null);
  const [loading, setLoading] = useState(true);

  // Topic practice state
  const [startingTopic, setStartingTopic] = useState(null);

  // Mock test state
  const [startingMock, setStartingMock] = useState(false);

  // Study plan state
  const [plan, setPlan] = useState(null);
  const [planForm, setPlanForm] = useState({
    target_date: "",
    hours_per_day: 3,
    current_level: "beginner",
  });
  const [generatingPlan, setGeneratingPlan] = useState(false);

  // Doubt solver state
  const [doubt, setDoubt] = useState("");
  const [doubtAnswer, setDoubtAnswer] = useState(null);
  const [askingDoubt, setAskingDoubt] = useState(false);

  useEffect(() => {
    fetchExam();
    fetchPlan();
  }, [examId]);

  const fetchExam = async () => {
    try {
      const res = await axios.get(`${API}/exam-prep/exam/${examId}`);
      setExam(res.data);
    } catch (err) {
      console.error(err);
      toast.error("Failed to load exam details");
      navigate("/exam-prep");
    } finally {
      setLoading(false);
    }
  };

  const fetchPlan = async () => {
    if (!token) return;
    try {
      const res = await axios.get(`${API}/exam-prep/study-plan/${examId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.data.has_plan) setPlan(res.data.plan);
    } catch (err) {
      // No plan yet — fine
    }
  };

  const startTopicPractice = async (topic, sectionName) => {
    setStartingTopic(topic);
    try {
      const res = await axios.post(
        `${API}/exam-prep/practice/start`,
        {
          exam_id: examId,
          topic,
          question_count: 10,
          difficulty: "mixed",
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`${sectionName} practice started!`);
      navigate(`/exam-prep/practice/${res.data.session_id}`);
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.detail || "Failed to start practice");
    } finally {
      setStartingTopic(null);
    }
  };

  const startMockTest = async () => {
    if (!window.confirm(
      `This is a timed full-length mock test.\n\n` +
      `Duration: ${exam.total_duration_minutes} minutes\n` +
      `Questions: ${exam.total_questions}\n\n` +
      `Questions will be AI-generated — this may take 1-2 minutes. Continue?`
    )) return;
    
    setStartingMock(true);
    try {
      const res = await axios.post(
        `${API}/exam-prep/mock-test/start`,
        { exam_id: examId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Mock test started! Good luck.");
      navigate(`/exam-prep/mock-test/${res.data.session_id}`);
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.detail || "Failed to start mock test");
    } finally {
      setStartingMock(false);
    }
  };

  const generatePlan = async () => {
    if (!planForm.target_date) {
      toast.error("Please pick a target date");
      return;
    }
    setGeneratingPlan(true);
    try {
      const targetIso = new Date(planForm.target_date).toISOString();
      const res = await axios.post(
        `${API}/exam-prep/study-plan/generate`,
        {
          exam_id: examId,
          target_date: targetIso,
          hours_per_day: planForm.hours_per_day,
          current_level: planForm.current_level,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setPlan(res.data);
      toast.success(`${res.data.plan.length}-day plan generated!`);
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.detail || "Failed to generate plan");
    } finally {
      setGeneratingPlan(false);
    }
  };

  const markDayComplete = async (dayNumber) => {
    if (!plan?.id) return;
    try {
      const res = await axios.post(
        `${API}/exam-prep/study-plan/${plan.id}/complete-day/${dayNumber}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setPlan({ ...plan, completed_days: res.data.completed_days });
      toast.success(`Day ${dayNumber} marked complete!`);
    } catch (err) {
      toast.error("Failed to mark day complete");
    }
  };

  const askDoubt = async () => {
    if (!doubt.trim()) return;
    setAskingDoubt(true);
    setDoubtAnswer(null);
    try {
      const res = await axios.post(
        `${API}/exam-prep/doubt`,
        { exam_id: examId, doubt },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setDoubtAnswer(res.data.answer);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to get answer");
    } finally {
      setAskingDoubt(false);
    }
  };

  if (loading || !exam) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
        <Navbar />
        <div className="flex items-center justify-center py-24">
          <RefreshCw className="w-8 h-8 animate-spin text-blue-600" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      <Navbar />
      <div className="py-8 px-4">
      <div className="max-w-6xl mx-auto">
        <Button
          variant="ghost"
          onClick={() => navigate("/exam-prep")}
          className="mb-4"
          data-testid="back-to-catalog-btn"
        >
          <ArrowLeft className="w-4 h-4 mr-2" /> Back to Exams
        </Button>

        {/* Exam Header */}
        <Card className="mb-6 border-0 shadow-lg overflow-hidden" data-testid="exam-detail-header">
          <div className="h-2 bg-gradient-to-r from-blue-500 via-indigo-500 to-purple-500" />
          <CardHeader>
            <div className="flex items-start justify-between flex-wrap gap-3">
              <div>
                <CardTitle className="text-2xl sm:text-3xl">{exam.name}</CardTitle>
                <div className="flex items-center gap-2 mt-2 flex-wrap">
                  <Badge variant="outline">{exam.category}</Badge>
                  <Badge className="bg-yellow-100 text-yellow-800">{exam.difficulty}</Badge>
                  <Badge variant="outline">{exam.level}</Badge>
                </div>
                <CardDescription className="mt-3 text-base">{exam.description}</CardDescription>
              </div>
              <div className="text-right text-sm space-y-1">
                <div className="flex items-center gap-2 text-gray-700">
                  <FileText className="w-4 h-4" />
                  <span>{exam.total_questions} Qs</span>
                </div>
                <div className="flex items-center gap-2 text-gray-700">
                  <Clock className="w-4 h-4" />
                  <span>{exam.total_duration_minutes} min</span>
                </div>
              </div>
            </div>
          </CardHeader>
        </Card>

        {/* Tabs */}
        <Tabs defaultValue="topics" className="w-full">
          <TabsList className="grid grid-cols-4 w-full mb-4">
            <TabsTrigger value="topics" data-testid="tab-topics">
              <BookOpen className="w-4 h-4 mr-1 hidden sm:inline" /> Topics
            </TabsTrigger>
            <TabsTrigger value="mock-test" data-testid="tab-mock-test">
              <ClipboardList className="w-4 h-4 mr-1 hidden sm:inline" /> Mock Test
            </TabsTrigger>
            <TabsTrigger value="study-plan" data-testid="tab-study-plan">
              <CalendarDays className="w-4 h-4 mr-1 hidden sm:inline" /> Study Plan
            </TabsTrigger>
            <TabsTrigger value="doubt" data-testid="tab-doubt">
              <MessageSquareQuote className="w-4 h-4 mr-1 hidden sm:inline" /> Ask AI
            </TabsTrigger>
          </TabsList>

          {/* Topics Tab */}
          <TabsContent value="topics" className="space-y-4">
            {exam.sections.map((section, sIdx) => (
              <Card key={sIdx} className="border-0 shadow-md">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg">{section.name}</CardTitle>
                    <Badge className="bg-blue-100 text-blue-800">
                      {section.question_count} Qs in exam
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                    {section.topics.map((topic, tIdx) => (
                      <button
                        key={tIdx}
                        disabled={startingTopic === topic}
                        onClick={() => startTopicPractice(topic, section.name)}
                        className="flex items-center justify-between p-3 bg-gradient-to-r from-white to-blue-50 rounded-lg border border-blue-100 hover:border-blue-400 hover:shadow-md transition-all text-left disabled:opacity-50"
                        data-testid={`topic-btn-${topic.replace(/\W+/g, "-")}`}
                      >
                        <span className="text-sm font-medium text-gray-800">
                          {topic}
                        </span>
                        {startingTopic === topic ? (
                          <RefreshCw className="w-4 h-4 animate-spin text-blue-600 shrink-0" />
                        ) : (
                          <Play className="w-4 h-4 text-blue-600 shrink-0" />
                        )}
                      </button>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </TabsContent>

          {/* Mock Test Tab */}
          <TabsContent value="mock-test">
            <Card className="border-0 shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <ClipboardList className="w-5 h-5 text-indigo-600" />
                  Full-Length Mock Test
                </CardTitle>
                <CardDescription>
                  Simulate the actual {exam.name} exam in a timed environment.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  <InfoTile label="Total Questions" value={exam.total_questions} />
                  <InfoTile label="Duration" value={`${exam.total_duration_minutes} min`} />
                  <InfoTile label="Sections" value={exam.sections.length} />
                  <InfoTile
                    label="Negative Marking"
                    value={exam.negative_marking || "None"}
                  />
                </div>
                <div className="space-y-2">
                  <p className="text-sm font-semibold text-gray-800">Section Breakdown:</p>
                  {exam.sections.map((s, i) => (
                    <div
                      key={i}
                      className="flex justify-between items-center text-sm p-2 bg-gray-50 rounded"
                    >
                      <span>{s.name}</span>
                      <span className="font-bold">{s.question_count} Qs</span>
                    </div>
                  ))}
                </div>
                <Button
                  onClick={startMockTest}
                  disabled={startingMock}
                  size="lg"
                  className="w-full bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700"
                  data-testid="start-mock-test-btn"
                >
                  {startingMock ? (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      Generating {exam.total_questions} questions...
                    </>
                  ) : (
                    <>
                      <Play className="w-4 h-4 mr-2" />
                      Start Mock Test
                    </>
                  )}
                </Button>
                <p className="text-xs text-gray-500 text-center">
                  💡 AI generates fresh questions each attempt — quality-validated via 2-pass LLM review
                </p>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Study Plan Tab */}
          <TabsContent value="study-plan">
            {!plan ? (
              <Card className="border-0 shadow-lg">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Sparkles className="w-5 h-5 text-purple-600" />
                    Generate AI Study Plan
                  </CardTitle>
                  <CardDescription>
                    Get a day-wise personalized plan based on your target date and availability.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div>
                      <Label htmlFor="target-date">Target Exam Date</Label>
                      <Input
                        type="date"
                        id="target-date"
                        value={planForm.target_date}
                        onChange={(e) =>
                          setPlanForm({ ...planForm, target_date: e.target.value })
                        }
                        min={new Date().toISOString().split("T")[0]}
                        data-testid="plan-target-date"
                      />
                    </div>
                    <div>
                      <Label htmlFor="hours">Hours/Day</Label>
                      <Input
                        type="number"
                        id="hours"
                        min="0.5"
                        max="12"
                        step="0.5"
                        value={planForm.hours_per_day}
                        onChange={(e) =>
                          setPlanForm({
                            ...planForm,
                            hours_per_day: parseFloat(e.target.value) || 3,
                          })
                        }
                        data-testid="plan-hours"
                      />
                    </div>
                    <div>
                      <Label htmlFor="level">Your Level</Label>
                      <select
                        id="level"
                        value={planForm.current_level}
                        onChange={(e) =>
                          setPlanForm({ ...planForm, current_level: e.target.value })
                        }
                        className="w-full p-2 border border-gray-300 rounded-md h-10"
                        data-testid="plan-level"
                      >
                        <option value="beginner">Beginner</option>
                        <option value="intermediate">Intermediate</option>
                        <option value="advanced">Advanced</option>
                      </select>
                    </div>
                  </div>
                  <Button
                    onClick={generatePlan}
                    disabled={generatingPlan}
                    size="lg"
                    className="w-full bg-gradient-to-r from-purple-600 to-pink-600"
                    data-testid="generate-plan-btn"
                  >
                    {generatingPlan ? (
                      <>
                        <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                        Generating personalized plan...
                      </>
                    ) : (
                      <>
                        <Sparkles className="w-4 h-4 mr-2" />
                        Generate My Study Plan
                      </>
                    )}
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <StudyPlanView
                plan={plan}
                onMarkComplete={markDayComplete}
                onRegenerate={() => setPlan(null)}
              />
            )}
          </TabsContent>

          {/* Doubt Solver Tab */}
          <TabsContent value="doubt">
            <Card className="border-0 shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <MessageSquareQuote className="w-5 h-5 text-emerald-600" />
                  Ask AI Doubt Solver
                </CardTitle>
                <CardDescription>
                  Stuck on any topic from {exam.name}? Ask and get step-by-step explanations.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                <textarea
                  rows={4}
                  value={doubt}
                  onChange={(e) => setDoubt(e.target.value)}
                  placeholder={`E.g., "Explain how to calculate compound interest with examples" or "What is the difference between Syllogism types?"`}
                  className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-emerald-500 resize-none"
                  data-testid="doubt-input"
                />
                <Button
                  onClick={askDoubt}
                  disabled={askingDoubt || !doubt.trim()}
                  className="w-full bg-emerald-600 hover:bg-emerald-700"
                  data-testid="ask-doubt-btn"
                >
                  {askingDoubt ? (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      Thinking...
                    </>
                  ) : (
                    "Get Answer"
                  )}
                </Button>
                {doubtAnswer && (
                  <Card className="bg-gradient-to-br from-emerald-50 to-teal-50 border-emerald-200">
                    <CardContent className="pt-4">
                      <p className="text-sm font-semibold text-emerald-900 mb-2">AI Explanation:</p>
                      <div
                        className="text-sm text-gray-800 whitespace-pre-wrap"
                        data-testid="doubt-answer"
                      >
                        {doubtAnswer}
                      </div>
                    </CardContent>
                  </Card>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
      </div>
    </div>
  );
}

const InfoTile = ({ label, value }) => (
  <div className="bg-gradient-to-br from-blue-50 to-indigo-50 p-3 rounded-lg text-center">
    <p className="text-xs text-gray-600">{label}</p>
    <p className="text-lg font-bold text-blue-700">{value}</p>
  </div>
);

const StudyPlanView = ({ plan, onMarkComplete, onRegenerate }) => {
  const completed = new Set(plan.completed_days || []);
  const days = plan.plan || [];
  const progressPct = days.length ? Math.round((completed.size / days.length) * 100) : 0;

  return (
    <Card className="border-0 shadow-lg" data-testid="study-plan-view">
      <CardHeader>
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <CardTitle>Your {days.length}-Day Plan</CardTitle>
            <CardDescription>
              Target: {new Date(plan.target_date).toLocaleDateString()} • {plan.hours_per_day} hrs/day
            </CardDescription>
          </div>
          <Button variant="outline" size="sm" onClick={onRegenerate}>
            <RefreshCw className="w-3 h-3 mr-1" /> Regenerate
          </Button>
        </div>
        <div className="mt-4 bg-purple-50 p-3 rounded-md">
          <div className="flex items-center justify-between text-sm mb-1">
            <span className="font-semibold">Progress</span>
            <span className="text-purple-700 font-bold">
              {completed.size}/{days.length} days • {progressPct}%
            </span>
          </div>
          <div className="w-full bg-white rounded-full h-2 overflow-hidden">
            <div
              className="bg-gradient-to-r from-purple-500 to-pink-500 h-full transition-all"
              style={{ width: `${progressPct}%` }}
            />
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-2 max-h-[600px] overflow-y-auto pr-2">
          {days.map((day, i) => {
            const dayNum = day.day_number || i + 1;
            const isCompleted = completed.has(dayNum);
            return (
              <div
                key={i}
                className={`p-3 rounded-lg border transition-all ${
                  isCompleted
                    ? "bg-green-50 border-green-200"
                    : "bg-white border-gray-200 hover:border-purple-300"
                }`}
                data-testid={`plan-day-${dayNum}`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <Badge variant="outline" className="text-xs">
                        {day.date_label || `Day ${dayNum}`}
                      </Badge>
                      <span className="text-xs text-gray-600">
                        {day.target_hours}hrs
                      </span>
                    </div>
                    <p className="font-semibold text-gray-900 text-sm">{day.focus_area}</p>
                    {day.topics && day.topics.length > 0 && (
                      <p className="text-xs text-gray-600 mt-0.5">
                        Topics: {day.topics.join(", ")}
                      </p>
                    )}
                    {day.activities && (
                      <ul className="mt-1.5 text-xs text-gray-700 space-y-0.5">
                        {day.activities.map((a, idx) => (
                          <li key={idx} className="flex items-start gap-1">
                            <ChevronRight className="w-3 h-3 mt-0.5 text-purple-600 shrink-0" />
                            <span>{a}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                    {day.milestone && (
                      <p className="text-xs italic text-purple-700 mt-1">
                        🎯 {day.milestone}
                      </p>
                    )}
                  </div>
                  <Button
                    size="sm"
                    variant={isCompleted ? "default" : "outline"}
                    onClick={() => !isCompleted && onMarkComplete(dayNum)}
                    disabled={isCompleted}
                    className={isCompleted ? "bg-green-600 hover:bg-green-600" : ""}
                    data-testid={`mark-day-${dayNum}`}
                  >
                    {isCompleted ? (
                      <>
                        <CheckCircle2 className="w-3 h-3 mr-1" /> Done
                      </>
                    ) : (
                      "Mark Done"
                    )}
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
};
